import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RESTAURANT } from '../venues';

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
};

export default function LandingScreen() {
  const primaryHref = '/app/home';
  const primaryLabel = 'В приложение';

  useEffect(() => {
    document.documentElement.classList.add('landing-page');
    const prevTitle = document.title;
    document.title = RESTAURANT.title;
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute('content') ?? '';
    meta?.setAttribute('content', RESTAURANT.description);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    const prevCanonical = canonical.getAttribute('href');
    canonical.setAttribute('href', RESTAURANT.url);

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'restaurant-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Restaurant',
      name: RESTAURANT.name,
      description: RESTAURANT.description,
      url: RESTAURANT.url,
      telephone: '+74955762090',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'ул. Первомайская, 9/4',
        addressLocality: 'Долгопрудный',
        addressCountry: 'RU',
      },
    });
    document.getElementById('restaurant-jsonld')?.remove();
    document.head.appendChild(script);

    return () => {
      document.documentElement.classList.remove('landing-page');
      document.title = prevTitle;
      meta?.setAttribute('content', prevDesc);
      if (prevCanonical) canonical?.setAttribute('href', prevCanonical);
      script.remove();
    };
  }, []);

  return (
    <div className="landing-root min-h-screen text-white" style={{ background: '#0a0a0a' }}>
      {/* Hero */}
      <section className="relative min-h-[100svh] flex flex-col overflow-hidden">
        <img
          src="/landing/bar_cover.png"
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-[22%_center] sm:object-center"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 38%, rgba(0,0,0,0.72) 72%, #0a0a0a 100%)',
          }}
        />

        <header className="relative z-10 flex items-center justify-between gap-4 px-5 sm:px-10 pt-6">
          <span
            className="text-[11px] tracking-[0.28em] uppercase"
            style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Manrope, sans-serif' }}
          >
            Долгопрудный
          </span>
          <nav className="flex items-center gap-5 shrink-0">
            <Link
              to="/booking"
              className="text-[12px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap transition-opacity hover:opacity-70"
              style={{ fontFamily: 'Manrope, sans-serif', color: 'rgba(255,255,255,0.7)' }}
            >
              Бронь
            </Link>
            <Link
              to="/events"
              className="text-[12px] tracking-[0.18em] uppercase font-semibold whitespace-nowrap transition-opacity hover:opacity-70"
              style={{ fontFamily: 'Manrope, sans-serif', color: 'rgba(255,255,255,0.7)' }}
            >
              События
            </Link>
          </nav>
        </header>

        <div className="relative z-10 flex-1 flex flex-col justify-end px-5 sm:px-10 pb-14 sm:pb-20 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 36 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] as const }}
          >
            <h1
              className="text-[18vw] sm:text-[9rem] leading-[0.85] font-bold uppercase mb-3"
              style={{
                fontFamily: 'Unbounded, sans-serif',
                letterSpacing: '-0.04em',
              }}
            >
              Теория
            </h1>
            <p
              className="text-[11px] sm:text-xs tracking-[0.35em] uppercase mb-5"
              style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Manrope, sans-serif' }}
            >
              Кухня &amp; бар · ресторан · с 2017
            </p>
            <p
              className="max-w-md text-base sm:text-lg leading-relaxed mb-8"
              style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'Manrope, sans-serif' }}
            >
              Интеллектуальное городское пространство у кампуса: кухня, бар и место для встреч.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/menu"
                className="inline-flex items-center justify-center px-7 py-3.5 text-[13px] tracking-[0.16em] uppercase font-semibold transition-transform active:scale-[0.98]"
                style={{
                  background: '#fff',
                  color: '#0a0a0a',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                Меню
              </Link>
              <Link
                to="/events"
                className="inline-flex items-center justify-center px-7 py-3.5 text-[13px] tracking-[0.16em] uppercase font-semibold transition-opacity hover:opacity-80"
                style={{
                  border: '1px solid rgba(255,255,255,0.45)',
                  color: '#fff',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                События
              </Link>
              <Link
                to="/booking"
                className="inline-flex items-center justify-center px-7 py-3.5 text-[13px] tracking-[0.16em] uppercase font-semibold transition-opacity hover:opacity-80"
                style={{
                  border: '1px solid rgba(255,255,255,0.45)',
                  color: '#fff',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                Бронь
              </Link>
              <Link
                to={primaryHref}
                className="inline-flex items-center justify-center px-7 py-3.5 text-[13px] tracking-[0.16em] uppercase font-semibold transition-opacity hover:opacity-80"
                style={{
                  border: '1px solid rgba(255,255,255,0.45)',
                  color: '#fff',
                  fontFamily: 'Manrope, sans-serif',
                }}
              >
                {primaryLabel}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About */}
      <section className="px-5 sm:px-10 py-20 sm:py-28 max-w-5xl mx-auto">
        <motion.div {...fadeUp}>
          <p
            className="text-[11px] tracking-[0.3em] uppercase mb-6"
            style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
          >
            О проекте
          </p>
          <h2
            className="text-3xl sm:text-5xl font-bold leading-[1.1] mb-8 max-w-3xl"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
          >
            Стильный лофт для встреч, мастер-классов и спокойной работы
          </h2>
          <p
            className="max-w-2xl text-base sm:text-lg leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.58)', fontFamily: 'Manrope, sans-serif' }}
          >
            Стильный лофт, где можно провести бизнес-встречу, сходить на мастер-класс всей семьёй,
            посетить дегустацию виски или научный семинар, отметить годовщину с друзьями или спокойно
            поработать.
          </p>
        </motion.div>
      </section>

      {/* Atmosphere */}
      <section className="relative h-[70vh] min-h-[420px] overflow-hidden">
        <img
          src="/landing/interior.png"
          alt="Интерьер ресторана Теория"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, #0a0a0a 0%, transparent 25%, transparent 70%, #0a0a0a 100%)',
          }}
        />
        <div className="absolute bottom-10 left-5 sm:left-10 right-5 max-w-xl">
          <motion.p
            {...fadeUp}
            className="text-xl sm:text-2xl font-semibold leading-snug"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
          >
            Строгий интерьер. Спокойный свет. Пространство для разговора.
          </motion.p>
        </div>
      </section>

      {/* Kitchen */}
      <section className="grid md:grid-cols-2 min-h-[520px]">
        <div className="relative min-h-[320px] overflow-hidden">
          <img
            src="/landing/kitchen.png"
            alt="Кухня ресторана Теория"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        </div>
        <div className="flex flex-col justify-center px-5 sm:px-12 py-16 sm:py-20">
          <motion.div {...fadeUp}>
            <p
              className="text-[11px] tracking-[0.3em] uppercase mb-5"
              style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
            >
              Кухня
            </p>
            <h2
              className="text-3xl sm:text-4xl font-bold mb-6 leading-tight"
              style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
            >
              Азиатская кухня, гриль, пицца и вкусный кофе
            </h2>
            <p
              className="text-base leading-relaxed mb-8"
              style={{ color: 'rgba(255,255,255,0.58)', fontFamily: 'Manrope, sans-serif' }}
            >
              Теория объединяет околоуниверситетское сообщество вкусной едой, высоким уровнем
              сервиса и актуальным познавательно-развлекательным контентом.
            </p>
            <Link
              to="/menu"
              className="inline-flex self-start text-[13px] tracking-[0.16em] uppercase font-semibold border-b border-white/40 pb-1 hover:border-white transition-colors"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Смотреть меню
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Wine & events */}
      <section className="grid md:grid-cols-2 min-h-[520px]">
        <div className="flex flex-col justify-center px-5 sm:px-12 py-16 sm:py-20 order-2 md:order-1">
          <motion.div {...fadeUp}>
            <p
              className="text-[11px] tracking-[0.3em] uppercase mb-5"
              style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
            >
              Бар и события
            </p>
            <h2
              className="text-3xl sm:text-4xl font-bold mb-6 leading-tight"
              style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
            >
              Вино и виски. Презентации и дегустации
            </h2>
            <p
              className="text-base leading-relaxed mb-8"
              style={{ color: 'rgba(255,255,255,0.58)', fontFamily: 'Manrope, sans-serif' }}
            >
              Кулинарные мастер-классы, дегустации вина и виски, арт-вечера — познавательно-развлекательный
              контент для гостей ресторана.
            </p>
            <Link
              to="/events"
              className="inline-flex self-start text-[13px] tracking-[0.16em] uppercase font-semibold border-b border-white/40 pb-1 hover:border-white transition-colors"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Афиша и запись
            </Link>
          </motion.div>
        </div>
        <div className="relative min-h-[320px] overflow-hidden order-1 md:order-2">
          <img
            src="/landing/wine.png"
            alt="Бар Теория"
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
        </div>
      </section>

      {/* Space strip */}
      <section className="relative h-[50vh] min-h-[320px] overflow-hidden">
        <img
          src="/landing/space.png"
          alt="Пространство ресторана"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 flex items-center justify-center px-5 text-center">
          <motion.h2
            {...fadeUp}
            className="text-2xl sm:text-4xl font-bold max-w-2xl leading-tight"
            style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.03em' }}
          >
            Для встреч, которые хочется продолжить
          </motion.h2>
        </div>
      </section>

      {/* Contact / footer */}
      <footer className="px-5 sm:px-10 pt-20 pb-12 border-t border-white/10">
        <div className="max-w-5xl mx-auto grid sm:grid-cols-2 gap-12">
          <div>
            <p
              className="text-[11px] tracking-[0.3em] uppercase mb-4"
              style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
            >
              Адрес
            </p>
            <p
              className="text-xl sm:text-2xl font-bold mb-3"
              style={{ fontFamily: 'Unbounded, sans-serif', letterSpacing: '-0.02em' }}
            >
              г. Долгопрудный,
              <br />
              ул. Первомайская, 9/4
            </p>
            <p
              className="text-sm leading-relaxed"
              style={{ color: 'rgba(255,255,255,0.45)', fontFamily: 'Manrope, sans-serif' }}
            >
              ООО «Ресторан Теория»
              <br />
              ОГРН 1175029010650 · ИНН 5047197150
            </p>
          </div>
          <div className="sm:text-right flex flex-col sm:items-end justify-between gap-8">
            <div>
              <p
                className="text-[11px] tracking-[0.3em] uppercase mb-4"
                style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Manrope, sans-serif' }}
              >
                Контакты
              </p>
              <a
                href="tel:+74955762090"
                className="block text-lg font-semibold mb-2 hover:opacity-70 transition-opacity"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                +7 495 576-20-90
              </a>
              <a
                href="mailto:manager@theorybar.ru"
                className="block text-sm hover:opacity-70 transition-opacity"
                style={{ color: 'rgba(255,255,255,0.55)', fontFamily: 'Manrope, sans-serif' }}
              >
                manager@theorybar.ru
              </a>
            </div>
            <div className="flex flex-wrap gap-4 sm:justify-end text-[12px] tracking-[0.12em] uppercase">
              <Link to="/menu" className="hover:opacity-70 transition-opacity" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Меню
              </Link>
              <Link to="/booking" className="hover:opacity-70 transition-opacity" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Бронь
              </Link>
              <Link to="/events" className="hover:opacity-70 transition-opacity" style={{ fontFamily: 'Manrope, sans-serif' }}>
                События
              </Link>
              <Link to={primaryHref} className="hover:opacity-70 transition-opacity" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {primaryLabel}
              </Link>
              <Link to="/privacy-policy" className="hover:opacity-70 transition-opacity" style={{ fontFamily: 'Manrope, sans-serif', color: 'rgba(255,255,255,0.4)' }}>
                Политика
              </Link>
            </div>
          </div>
        </div>
        <p
          className="max-w-5xl mx-auto mt-14 text-[11px] tracking-[0.2em] uppercase"
          style={{ color: 'rgba(255,255,255,0.28)', fontFamily: 'Manrope, sans-serif' }}
        >
          © {new Date().getFullYear()} Теория · Кухня &amp; бар
        </p>
      </footer>
    </div>
  );
}
